import { formattedDollarsToCents } from '@fruitful/fruitils';
import type {
    MembershipIncome,
    MembershipIncomeUpsertDto,
} from '@fruitful/member-profile';

import {
    buildUpsertDto,
    type IncomeDetailsFormState,
} from './moneyMapperIncomeForm';

export type IncomeFormUpsertValidationFailureReason =
    | 'empty_description'
    | 'invalid_amount';

export type IncomeFormUpsertValidationResult =
    | { ok: true }
    | { ok: false; reason: IncomeFormUpsertValidationFailureReason };

/** Toast copy when the details modal blocks save (inline table/new row stay silent). */
export const incomeFormValidationModalToastMessage: Record<
    IncomeFormUpsertValidationFailureReason,
    string
> = {
    empty_description: 'Enter a source.',
    invalid_amount: 'Enter a valid amount.',
};

export function isIncomeSourceEmpty(description: string): boolean {
    return description.trim().length === 0;
}

export function isIncomePeriodAmountInvalid(periodAmountStr: string): boolean {
    const periodCents = formattedDollarsToCents(periodAmountStr);
    return Number.isNaN(periodCents) || periodCents <= 0;
}

export function validateIncomeFormForUpsert(
    form: IncomeDetailsFormState,
): IncomeFormUpsertValidationResult {
    if (isIncomeSourceEmpty(form.description)) {
        return { ok: false, reason: 'empty_description' };
    }
    if (isIncomePeriodAmountInvalid(form.periodAmountStr)) {
        return { ok: false, reason: 'invalid_amount' };
    }
    return { ok: true };
}

type UpsertMembershipIncomeAsync = (
    membershipId: string,
    body: MembershipIncomeUpsertDto[],
) => Promise<unknown>;

/**
 * Builds the upsert DTO from form state, optionally merges overrides (modal notes),
 * upserts, then refetches membership and syncs money map monthly income.
 */
export async function persistMembershipIncomeFromForm({
    upsertMembershipIncomeAsync,
    refetchMembershipAndSyncMoneyMap,
    membershipId,
    existing,
    form,
    dtoOverrides,
    afterUpsertBeforeRefetch,
}: {
    upsertMembershipIncomeAsync: UpsertMembershipIncomeAsync;
    refetchMembershipAndSyncMoneyMap: () => Promise<void>;
    membershipId: string;
    existing: MembershipIncome | null;
    form: IncomeDetailsFormState;
    dtoOverrides?: Partial<MembershipIncomeUpsertDto>;
    /** e.g. hide the new-income row before refetch so the list does not briefly show duplicates. */
    afterUpsertBeforeRefetch?: () => void;
}): Promise<void> {
    const dto = buildUpsertDto({ existing, membershipId, form });
    const body = dtoOverrides != null ? { ...dto, ...dtoOverrides } : dto;
    await upsertMembershipIncomeAsync(membershipId, [body]);
    afterUpsertBeforeRefetch?.();
    await refetchMembershipAndSyncMoneyMap();
}
